/**
 * Helpers PURS de publication Instagram Reels (et futurs Reels Meta).
 *
 * - `buildReelsCaption` : légende par défaut d'un Reel — réutilisée par l'UI
 *   (pré-remplissage éditable de l'éditeur) et par le worker (valeur de repli
 *   quand `youtube_videos.social_caption` est NULL).
 * - `reelsCreateBody` / `parseContainerStatus` : façonnage et lecture des
 *   réponses du Graph API Meta (flux REELS), isolés ici pour être testés sans
 *   réseau. Le worker (apps/video-worker) les consomme via `@jeraisonne/db/youtube`.
 */

import { DEFAULT_APP_URL, exerciseAppUrl } from './description';
import { escapeDrawtext } from './filtergraph';
import type { VideoOverlay } from '../schemas/youtube';

export interface ReelsCaptionInput {
  /** Titre de l'exercice (1re ligne, accroche). */
  title: string;
  /** Cible / public visé (ex. « Pour les 4e qui révisent Pythagore »). Optionnel. */
  target?: string | null;
  /** Niveau scolaire affiché (ex. « 4e »). Optionnel. */
  level?: string | null;
  /** Matière (ex. « Maths ») — alimente un hashtag. Optionnel. */
  subject?: string | null;
  /** Lien « pour essayer ». Défaut {@link DEFAULT_APP_URL}. */
  url?: string | null;
}

/** Slugifie un libellé en hashtag ASCII compact (accents retirés, espaces collés). */
function toHashtag(label: string): string {
  const ascii = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // marques diacritiques combinantes
    .replace(/[^a-zA-Z0-9]/g, '');
  return ascii ? `#${ascii}` : '';
}

/**
 * Construit la légende par défaut d'un Reel :
 *
 *   {titre}
 *
 *   🎯 {cible}
 *   📚 Niveau {niveau}
 *
 *   👉 Essaie l'exercice : {url}
 *
 *   #JeRaisonne #revisions #{matiere} #{niveau}
 *
 * Pur & déterministe (aucune date/aléa) → testable. Plafonné à 2200 caractères
 * (limite Instagram).
 */
export function buildReelsCaption(input: ReelsCaptionInput): string {
  const url = (input.url || DEFAULT_APP_URL).replace(/\/$/, '');
  const lines: string[] = [];

  const title = input.title.trim();
  if (title) lines.push(title);

  const meta: string[] = [];
  if (input.target?.trim()) meta.push(`🎯 ${input.target.trim()}`);
  if (input.level?.trim()) meta.push(`📚 Niveau ${input.level.trim()}`);
  if (meta.length > 0) lines.push('', ...meta);

  lines.push('', `👉 Essaie l'exercice : ${url}`);

  const tags = ['#JeRaisonne', '#revisions', '#maths'];
  if (input.subject?.trim()) tags.push(toHashtag(input.subject.trim()));
  if (input.level?.trim()) tags.push(toHashtag(input.level.trim()));
  const uniqueTags = [...new Set(tags.filter(Boolean))];
  lines.push('', uniqueTags.join(' '));

  return lines.join('\n').slice(0, 2200);
}

/**
 * Lien « pour essayer » par défaut d'un Reel : route démo anonyme de l'exercice.
 * Si `exerciseId` absent, repli sur la home de l'app.
 */
export function reelsTryUrl(exerciseId?: string | null, appUrl = DEFAULT_APP_URL): string {
  return exerciseId ? exerciseAppUrl(exerciseId, appUrl) : appUrl.replace(/\/$/, '');
}

// ── Défi social : légende « short » (typo des posts d'articles) ──────────────

export interface ChallengeShortCaptionInput {
  /** Titre du défi (1re ligne, accroche). */
  title: string;
  /** Énoncé de la question (corps). Markdown & LaTeX tolérés (nettoyés). Optionnel. */
  promptMd?: string | null;
  /** Lien public du défi (`/challenge/{slug}`). */
  challengeUrl: string;
}

/** Tronque sur une frontière de mot, suffixe « … » si coupé (calque social-copy.ts). */
function truncateWords(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** Aplatit un énoncé Markdown/LaTeX en une ligne lisible (légende plate). */
function plainPrompt(md: string): string {
  return md
    .replace(/\$\$?([^$]*)\$\$?/g, '$1') // délimiteurs LaTeX $…$ / $$…$$
    .replace(/\\[()[\]]/g, '') // \( \) \[ \]
    .replace(/[*_`#>]/g, '') // marqueurs markdown
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Légende d'un Short / Reel de DÉFI — calquée sur la « typo » des posts
 * d'articles (`_shared/social-copy.ts` : titre + corps + `👉 CTA : url`), SANS la
 * rafale de hashtags du reel d'exercice :
 *
 *   {titre du défi}
 *
 *   {question (énoncé nettoyé)}
 *
 *   👉 Relève le défi : {url}
 *
 * Pur & déterministe → testable. Plafonné à 2200 caractères (limite Instagram /
 * titre TikTok). Le CTA (avec l'URL) est toujours préservé (on tronque le corps).
 */
export function buildChallengeShortCaption(input: ChallengeShortCaptionInput): string {
  const cta = `\n\n👉 Relève le défi : ${input.challengeUrl.trim()}`;

  const blocks: string[] = [];
  const title = input.title.trim();
  if (title) blocks.push(title);
  const prompt = input.promptMd ? plainPrompt(input.promptMd) : '';
  if (prompt) blocks.push(prompt);

  const maxBody = Math.max(0, 2200 - cta.length);
  return `${truncateWords(blocks.join('\n\n'), maxBody)}${cta}`;
}

/** Lien public d'un défi : `{marketingUrl}/challenge/{slug}`. Défaut jeraisonne.fr. */
export function challengeShareUrl(slug: string, marketingUrl = DEFAULT_APP_URL): string {
  return `${marketingUrl.replace(/\/$/, '')}/challenge/${slug}`;
}

// ── Overlay d'un Reel (dérivé d'un exercice) ─────────────────────────────────

export interface ReelOverlayInput {
  /** Titre de l'exercice (incrusté en haut de la vidéo). */
  title: string;
  /** Niveau scolaire affiché (ex. « 4e »). */
  level?: string | null;
  /** Matière (ex. « Maths »). */
  subject?: string | null;
  /** Cible / accroche (sous-titre incrusté). */
  target?: string | null;
  /** Exercice cible → lien démo encodé dans le QR + affiché. */
  exerciseId?: string | null;
}

/**
 * Construit l'overlay (texte dessiné DANS la vidéo) d'un Reel à partir des
 * métadonnées de l'exercice. Logo + QR + URL toujours affichés (CTA « essaie »).
 * Pur → testable, partagé client (insert spec) / défauts.
 */
export function buildReelOverlay(input: ReelOverlayInput): VideoOverlay {
  return {
    title: (input.title || '').slice(0, 120),
    subtitle: (input.target || '').slice(0, 160),
    level: (input.level || '').slice(0, 40),
    subject: (input.subject || '').slice(0, 60),
    show_logo: true,
    show_qr: true,
    show_url: true,
    qr_url: reelsTryUrl(input.exerciseId),
  };
}

// ── Accroche en bas de la cover Reel (façon couverture d'article blog) ───────

/**
 * Découpe un texte en lignes courtes (FFmpeg drawtext ne fait pas de wrap auto).
 * Tronque à `maxLines` (… sur la dernière) ; un mot > `maxChars` occupe sa ligne.
 */
export function wrapReelText(text: string, maxChars = 22, maxLines = 3): string[] {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (!cur || candidate.length <= maxChars) cur = candidate;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = `${kept[maxLines - 1]}…`;
  return kept;
}

/**
 * Chaîne de filtres FFmpeg incrustant le texte de la cover Reel :
 *  - en HAUT, centrée : l'**accroche** (bande sombre + blanc gras) ;
 *  - en BAS, centré : le **niveau** puis le **titre** de l'exercice (bande sombre
 *    + blanc gras), bien lisibles sur le visuel.
 * PURE & testable. Renvoie '' si rien à dessiner. À chaîner après `scale/crop`.
 */
export function reelCoverText(opts: {
  fontEscaped: string;
  accroche?: string | null;
  level?: string | null;
  title?: string | null;
  width: number;
  height: number;
}): string {
  const f = opts.fontEscaped;
  const H = opts.height;
  const center = 'x=(w-text_w)/2';
  // Style « sticker » : blanc + contour noir ÉPAIS + ombre → gras, fun, lisible
  // sur n'importe quel fond (pas de bande sombre).
  const style = (size: number): string =>
    `fontcolor=white:fontsize=${size}:borderw=${Math.max(4, Math.round(size / 8))}:` +
    `bordercolor=black@0.92:shadowcolor=black@0.45:shadowx=0:shadowy=5`;
  const parts: string[] = [];

  // ── HAUT : accroche centrée, RAPPROCHÉE de la popup (≈14 % du haut) ──
  const accroche = (opts.accroche || '').trim();
  if (accroche) {
    const lines = wrapReelText(accroche, 22, 2);
    const fs = 66;
    const gap = fs + 18;
    const top = Math.round(H * 0.14);
    lines.forEach((line, i) => {
      parts.push(
        `drawtext=fontfile=${f}:text='${escapeDrawtext(line)}':${style(fs)}:${center}:y=${top + i * gap}`,
      );
    });
  }

  // ── BAS : niveau + titre, centrés, RAPPROCHÉS de la popup (bloc finissant ≈86 %) ──
  const bottom: Array<{ text: string; size: number }> = [];
  const level = (opts.level || '').trim();
  if (level) bottom.push({ text: level, size: 58 });
  wrapReelText((opts.title || '').trim(), 24, 2).forEach((l) => bottom.push({ text: l, size: 64 }));
  if (bottom.length > 0) {
    const gap = 22;
    const totalH = bottom.reduce((s, p) => s + p.size + gap, 0) - gap;
    const blockBottom = Math.round(H * 0.86);
    let y = blockBottom - totalH;
    for (const p of bottom) {
      parts.push(
        `drawtext=fontfile=${f}:text='${escapeDrawtext(p.text)}':${style(p.size)}:${center}:y=${y}`,
      );
      y += p.size + gap;
    }
  }

  return parts.join(',');
}

// ── Graph API Meta (flux REELS) ──────────────────────────────────────────────

/**
 * Corps du `POST /{ig-user-id}/media` pour créer un conteneur REELS.
 * `video_url` doit être une URL PUBLIQUEMENT téléchargeable (Meta la récupère
 * côté serveur — on passe une signed URL du master.mp4).
 */
export function reelsCreateBody(
  videoUrl: string,
  caption: string,
  coverUrl?: string | null,
): Record<string, string> {
  const body: Record<string, string> = {
    media_type: 'REELS',
    video_url: videoUrl,
    caption: caption.slice(0, 2200),
    // Partage aussi dans le feed principal (comportement attendu d'un Reel promo).
    share_to_feed: 'true',
  };
  // Vignette du Reel (écran bleu + titre + score). URL publique d'image 9:16.
  if (coverUrl) body.cover_url = coverUrl;
  return body;
}

export interface ContainerStatus {
  /** Code brut renvoyé par Meta (`status_code`). */
  statusCode: string;
  /** Le conteneur est prêt à être publié (`media_publish`). */
  finished: boolean;
  /** Le conteneur a échoué (ERROR / EXPIRED) — abandonner. */
  failed: boolean;
}

/**
 * Lit la réponse de `GET /{creation-id}?fields=status_code` du flux d'upload Meta.
 * `status_code` ∈ { EXPIRED, ERROR, FINISHED, IN_PROGRESS, PUBLISHED }.
 */
export function parseContainerStatus(json: unknown): ContainerStatus {
  const code = String((json as { status_code?: unknown })?.status_code ?? '').toUpperCase();
  return {
    statusCode: code,
    finished: code === 'FINISHED' || code === 'PUBLISHED',
    failed: code === 'ERROR' || code === 'EXPIRED',
  };
}
